import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { EMPTY_ARRAY_LENGTH, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    ICashFlowInvestingActivities,
    ICashFlowStatementInvestingEntry,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { localizationKeys } from '../../../constants/localizationKeys';
import { CashFlowStatementHelper } from '../../../helpers/cash-flow-statement.helper';
import { ICashFlowInvestingActivitiesComponentInput } from '../../../interfaces/ICashFlowInvestingActivitiesComponentInput.interface';
const debug = Debug('aumico:aumico-lib-annualreport-fe:CashFlowInvestingActivitiesComponent');
@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-cash-flow-investing-activities-component',
    templateUrl: 'cash-flow-investing-activities.component.html',
    styleUrls: ['cash-flow-investing-activities.component.scss'],
})
export class CashFlowInvestingActivitiesComponent implements OnInit {
    // refer to finNumber pipe for reference on values
    numberFormat = '1.2-2';
    JSON = JSON;

    private investingActivitiesData: ICashFlowInvestingActivitiesComponentInput;
    currentSelectedFliToDeleteIndex: number;
    showDeleteModal: boolean;
    @Input() set investingActivities(value: ICashFlowInvestingActivitiesComponentInput) {
        if (value != null && value.title != null && value.title.trim().length > EMPTY_ARRAY_LENGTH) {
            this.investingActivitiesTitle = value.title;
        }

        if (value && value.flis != null) {
            this.investingActivitiesData = cloneDeep(value);
            this.setTableHeaders();
            if (!this.editMode) {
                this.setInitialTableData();
                this.initializeForm();
            }
        }

        if (this.updateFlisAutomatically && this.investingActivitiesData) {
            this.updateFlis();
        }
    }

    private savedInvestingActivitiesData: ICashFlowInvestingActivities;
    @Input() set savedInvestingActivities(value: ICashFlowInvestingActivities) {
        if (value) {
            this.tableEntries = [];
            this.tableHasChanges = false;
            this.editMode = true;
            this.savedInvestingActivitiesData = cloneDeep(value);
            this.setSavedTableData();
            this.initializeForm();
            this.setTitleValue();
            if (this.updateFlisAutomatically) {
                this.updateFlis();
            }
        }
    }

    @Input() reportLocale: string;

    @Input() updateFlisAutomatically: boolean;

    private formValueChanges: Subscription | undefined;

    form: UntypedFormGroup;

    titleEditMode = false;
    private tableEntries: ICashFlowStatementInvestingEntry[] = [];
    private local = localizationKeys;
    currentYearDate: string;
    previousYearDate: string;
    currency: string;

    tableHasChanges = false;

    private editMode = false;

    dataSource: UntypedFormArray;

    private investmentsKeyValue: string;
    private disinvestmentsKeyValue: string;

    currentTotal: number = 0;
    previousTotal: number = 0;

    investingActivitiesTitle = '';

    @Output() totalChangeEvent = new EventEmitter<{ currentTotal: number; previousTotal: number }>();
    @Output() tableValidity = new EventEmitter<boolean>();
    constructor(private formBuilder: UntypedFormBuilder, private localizedTexts: I18NextService) {}

    ngOnInit(): void {
        debug('ngOnInit');
        this.applyLocalizations();
        this.initializeForm();
    }

    onSaveTitle($event: string): void {
        this.investingActivitiesTitle = $event;
        this.tableHasChanges = true;
    }

    /** trackBy for the group/child *ngFors: reuse DOM nodes by position instead of destroying and
     *  recreating every row on each form rebuild / change-detection pass — essential for large tables. */
    trackByIndex(index: number): number {
        return index;
    }

    /** REV-21417: above this many parent groups, render via a cdk-virtual-scroll viewport so only the
     *  visible rows are instantiated — a plain table with thousands of rows freezes / OOMs the tab. */
    readonly VIRTUAL_GROUP_THRESHOLD = 50;
    /** Fixed row height (px) for the virtual-scroll viewport — MUST match .cfs-vrow height in the SCSS. */
    readonly VIRTUAL_ROW_HEIGHT = 54;

    get isVirtualized(): boolean {
        return (this.dataSource?.controls?.length ?? 0) > this.VIRTUAL_GROUP_THRESHOLD;
    }

    /**
     * Flattens the nested parent/children groups into a single UNIFORM list of display rows so the
     * virtual-scroll viewport (fixed row height) can render only the visible rows. Each row carries
     * the form control(s) + group/child indices the existing handlers need. Pure/derived — rebuilt on
     * demand; the *cdkVirtualFor keeps the DOM stable via trackByIndex.
     */
    get investingDisplayRows(): Array<{
        kind: 'parent' | 'child' | 'addrow';
        group: UntypedFormGroup;
        child?: UntypedFormGroup;
        groupIndex: number;
        childIndex?: number;
    }> {
        const rows: Array<{
            kind: 'parent' | 'child' | 'addrow';
            group: UntypedFormGroup;
            child?: UntypedFormGroup;
            groupIndex: number;
            childIndex?: number;
        }> = [];
        const groups = this.dataSource?.controls ?? [];
        for (let g = 0; g < groups.length; g++) {
            const group = groups[g] as UntypedFormGroup;
            rows.push({ kind: 'parent', group, groupIndex: g });
            const children = (group.get('children') as UntypedFormArray)?.controls ?? [];
            for (let c = 0; c < children.length; c++) {
                rows.push({ kind: 'child', group, child: children[c] as UntypedFormGroup, groupIndex: g, childIndex: c });
            }
            rows.push({ kind: 'addrow', group, groupIndex: g });
        }
        return rows;
    }

    private applyLocalizations(): void {
        this.investmentsKeyValue = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_investing_activities_investments,
            { lng: this.reportLocale },
        ) as string;
        this.disinvestmentsKeyValue = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_investing_activities_disinvestments,
            { lng: this.reportLocale },
        ) as string;

        this.setTitleValue();
    }

    private setTitleValue(): void {
        if (
            !this.editMode &&
            (this.investingActivitiesTitle?.trim().length === EMPTY_ARRAY_LENGTH ||
                this.investingActivitiesTitle == null)
        ) {
            this.investingActivitiesTitle = this.localizedTexts.t(
                this.local.aumico_annual_report_cash_flow_investing_activities_title,
                {
                    lng: this.reportLocale,
                },
            ) as string;
        } else if (
            this.investingActivitiesTitle?.trim().length === EMPTY_ARRAY_LENGTH ||
            this.investingActivitiesTitle == null
        ) {
            this.investingActivitiesTitle = this.savedInvestingActivitiesData.title;
        }
    }

    private setTableHeaders(): void {
        this.currency = this.investingActivitiesData.currency;
        this.currentYearDate = this.investingActivitiesData.currentYearDate;
        this.previousYearDate = this.investingActivitiesData.previousYearDate;
    }

    private setInitialTableData(): void {
        this.tableEntries = [];
        this.prepareTableEntries();
    }

    private initializeForm(): void {
        this.form = this.formBuilder.group({
            dataSource: this.getGroupedEntriesAsFormGroup(),
        });

        this.dataSource = this.form.get('dataSource') as UntypedFormArray;

        // listen to form change
        this.listenToFormChange();
    }

    private addFormGroup(entry: ICashFlowStatementInvestingEntry): UntypedFormGroup {
        const dataSourceFormGroup = {};

        Object.keys(entry).forEach((key) => {
            dataSourceFormGroup[key] = new UntypedFormControl(entry[key]);
        });
        return new UntypedFormGroup(dataSourceFormGroup);
    }

    private getGroupedEntriesAsFormGroup(): UntypedFormArray {
        const groupedFormArray = this.formBuilder.array([]);

        const parentEntries = this.tableEntries.filter((entry) => !entry.parentFli && entry.fliId);
        parentEntries.forEach((parentEntry) => {
            const children = this.tableEntries.filter(
                (entry) =>
                    entry.parentFli != null &&
                    (entry.parentFli === parentEntry.fliId || entry.parentFli === parentEntry.name),
            );

            const childrenFormArray = children.length
                ? children
                : this.initChildEntries(parentEntry.fliId, parentEntry.isCustom);

            const parentFormGroup = this.formBuilder.group({
                parent: this.addFormGroup(parentEntry),
                children: this.formBuilder.array(childrenFormArray.map((child) => this.addFormGroup(child))),
            });

            groupedFormArray.push(parentFormGroup);
        });

        return groupedFormArray;
    }

    private initChildEntries(parentFliId: string, isCustom: boolean): ICashFlowStatementInvestingEntry[] {
        const investmentChild = {
            fliId: null,
            name: !isCustom
                ? (this.localizedTexts.t('aumico_annual_report_cash_flow_investing_activities_investments', {
                      lng: this.reportLocale,
                  }) as string)
                : null,
            calculatedPreviousValue: null,
            calculatedCurrentValue: null,
            staticLabel: false,
            labelEditMode: true,
            investing: true,
            disinvesting: false,
            parentFli: parentFliId,
            isCustom,
        };
        const disinvestmentChild = {
            fliId: null,
            name: !isCustom
                ? (this.localizedTexts.t('aumico_annual_report_cash_flow_investing_activities_disinvestments', {
                      lng: this.reportLocale,
                  }) as string)
                : null,
            calculatedPreviousValue: null,
            calculatedCurrentValue: null,
            staticLabel: false,
            labelEditMode: true,
            disinvesting: true,
            investing: false,
            parentFli: parentFliId,
            isCustom,
        };

        return [investmentChild, disinvestmentChild];
    }

    private addInvestingActivitiesEntry(parentFliId: string, isCustom: boolean): ICashFlowStatementInvestingEntry {
        const child = {
            fliId: null,
            name: null,
            calculatedPreviousValue: null,
            calculatedCurrentValue: null,
            staticLabel: false,
            labelEditMode: true,
            investing: true,
            disinvesting: false,
            parentFli: parentFliId,
            isCustom,
        };

        return child;
    }

    private listenToFormChange(): void {
        if (this.formValueChanges != null) {
            this.formValueChanges.unsubscribe();
        }

        this.formValueChanges = this.form.valueChanges
            .pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((value) => {
                // if it's already with true then no need to check
                if (this.tableHasChanges !== true) {
                    this.tableHasChanges = this.form.dirty;
                }

                const dataSource = value.dataSource.map((group) => [group.parent, ...group.children]).flat();
                this.tableEntries = cloneDeep<ICashFlowStatementInvestingEntry[]>(dataSource);

                // recalculating total
                this.setTotals();
                // checking validity
                const parents = value.dataSource.map((group) => group.parent);
                this.setValidity(parents);
            });
    }

    addFliBlock(): void {
        this.tableHasChanges = true;

        const dataSource = this.form.get('dataSource') as UntypedFormArray;
        const parentBlock = {
            fliId: uuidv4(),
            name: null,
            calculatedPreviousValue: null,
            calculatedCurrentValue: null,
            staticLabel: false,
            labelEditMode: true,
            investing: false,
            disinvesting: false,
            parentFli: null,
            isCustom: true,
        };

        const [investmentChild, disinvestmentChild] = this.initChildEntries(parentBlock.fliId, parentBlock.isCustom);

        this.tableEntries.push(parentBlock, investmentChild, disinvestmentChild);

        dataSource.push(
            new FormGroup({
                parent: this.addFormGroup(parentBlock),
                children: this.formBuilder.array(
                    [investmentChild, disinvestmentChild].map((child) => this.addFormGroup(child)),
                ),
            }),
        );
    }

    private prepareTableEntries(): void {
        if (this.investingActivitiesData != null) {
            const investingActivitiesData: ICashFlowInvestingActivitiesComponentInput =
                cloneDeep<ICashFlowInvestingActivitiesComponentInput>(this.investingActivitiesData);
            this.tableEntries = CashFlowStatementHelper.prepareTableEntriesForInvestingActivities(
                investingActivitiesData.flis,
                this.investmentsKeyValue,
                this.disinvestmentsKeyValue,
            );
        }
        this.setTotals();
    }

    private setSavedTableData(): void {
        this.tableEntries = cloneDeep(this.savedInvestingActivitiesData.entries);

        this.investingActivitiesTitle = this.savedInvestingActivitiesData.title;
        this.initializeForm();
        this.dataSource = this.form.get('dataSource') as UntypedFormArray;
        this.setTotals();
    }

    private setTotals(): void {
        const totals: { currentTotal: number; previousTotal: number } =
            CashFlowStatementHelper.calculateTableEntriesTotals(this.tableEntries);

        let currentTotal = 0;
        let previousTotal = 0;
        if (NumberUtils.isFinite(totals.currentTotal)) {
            currentTotal = NumberUtils.roundNumber(totals.currentTotal);
        }

        if (NumberUtils.isFinite(totals.previousTotal)) {
            previousTotal = NumberUtils.roundNumber(totals.previousTotal);
        }
        this.currentTotal = currentTotal;
        this.previousTotal = previousTotal;
        this.totalChangeEvent.emit({ currentTotal: this.currentTotal, previousTotal: this.previousTotal });
    }

    private setValidity(cashFlowEntries: ICashFlowStatementInvestingEntry[]): void {
        let isValid = true;
        for (const entry of cashFlowEntries) {
            // check that all parent names(static labels) are not left empty
            if (entry.name == null || entry.name === '') {
                isValid = false;
                break;
            }
        }

        this.tableValidity.emit(isValid);
    }

    getInvestingActivitiesFormValue(): ICashFlowInvestingActivities {
        const flattenDataSource = this.form?.value.dataSource.map((group) => [group.parent, ...group.children]).flat();
        const investingActivitiesOutput: ICashFlowInvestingActivities = {
            title: this.investingActivitiesTitle,
            entries: CashFlowStatementHelper.mapStringValuesInInvestingTableToNumbers(flattenDataSource),
        };

        return investingActivitiesOutput;
    }

    titleEdited(): void {
        this.titleEditMode = !this.titleEditMode;
    }

    private updateFlis(): void {
        if (this.investingActivitiesData) {
            this.tableEntries = CashFlowStatementHelper.updateInvestingActivitiesFlisValues(
                this.form?.value.dataSource,
                this.investingActivitiesData,
                this.investmentsKeyValue,
                this.disinvestmentsKeyValue,
            );

            this.dataSource = this.getGroupedEntriesAsFormGroup();

            this.form?.setControl('dataSource', this.dataSource);

            this.setTotals();
        }
    }

    removeGroup(index: number): void {
        this.currentSelectedFliToDeleteIndex = index;
        this.showDeleteModal = true;
        debug('remove index', index);
    }

    onCancelDelete(): void {
        this.currentSelectedFliToDeleteIndex = null;
        this.showDeleteModal = false;
    }

    onDeleteFliRow(): void {
        this.tableHasChanges = true;
        this.dataSource.removeAt(this.currentSelectedFliToDeleteIndex);
        this.currentSelectedFliToDeleteIndex = null;
        this.showDeleteModal = false;
    }

    onSaveFliName($event: string, index: number): void {
        this.tableHasChanges = true;
        this.tableEntries[index].name = $event;
        this.dataSource.controls[index].get('parent.name').setValue($event);
    }

    addRow(group: FormGroup): void {
        this.tableHasChanges = true;
        const children = group.get('children') as UntypedFormArray;
        const newChild = this.addInvestingActivitiesEntry(group.get('parent.fliId').value, true);
        children.push(this.addFormGroup(newChild));
    }

    removeRow(group: FormGroup, childIndex: number): void {
        this.tableHasChanges = true;
        const children = group.get('children') as UntypedFormArray;
        children.removeAt(childIndex);
    }
}
